import type {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  Redirect,
} from 'next'
import { getCertificateEnrollmentAccessState } from '@mc/api'
import { reportError } from '@mc/next-newrelic'
import { getUserAttributes } from '@mc/next-user-attributes'
import { paths } from '@mc/paths'
import {
  getCertificateProgramEnrollment,
  getCertificateUuid,
} from 'libs/server/api/certificates'
import { getCohortsEnabled } from 'pages/certificates/experiments/mccerts-3056_v1-cohorts'
import { resolveCohortState } from './enrollment/resolveCohortState'
import { getCohort } from './getCohort'
import type { Cohort, EnrollmentStateResult } from './types'

export type CohortPageProps = {
  kind: 'cohort'
  cohort: Cohort
  enrollment: EnrollmentStateResult
}

export const isCohortPageProps = (props: object): props is CohortPageProps =>
  'kind' in props && props.kind === 'cohort'

// Mirrors the certificate landing page's client-side check: learners with
// course access go to the overview, which routes Educator-powered programs on.
const getEnrolledLearnerRedirect = async (
  ctx: GetServerSidePropsContext,
  slug: string,
): Promise<{ redirect: Redirect } | null> => {
  if (!getUserAttributes(ctx, { query: ctx.query }).is_logged_in) {
    return null
  }

  try {
    const uuid = await getCertificateUuid(slug, ctx)
    if (!uuid) return null

    const accessState = getCertificateEnrollmentAccessState(
      await getCertificateProgramEnrollment(uuid, ctx),
    )
    if (
      accessState === 'active' ||
      accessState === 'consent_required' ||
      accessState === 'expired'
    ) {
      return {
        redirect: {
          destination: paths.certificateOverview(slug),
          permanent: false,
        },
      }
    }
  } catch (error) {
    reportError(error, { source: 'cohort-landing-enrollment', slug })
  }
  return null
}

export const getCohortPageProps = async (
  ctx: GetServerSidePropsContext,
  slug: string,
): Promise<GetServerSidePropsResult<CohortPageProps>> => {
  const enrolledRedirect = await getEnrolledLearnerRedirect(ctx, slug)
  if (enrolledRedirect) {
    return enrolledRedirect
  }

  if (!(await getCohortsEnabled(ctx))) {
    return { notFound: true }
  }

  const cohort = await getCohort(slug)

  if (!cohort) {
    return { notFound: true }
  }

  return {
    props: {
      kind: 'cohort',
      cohort,
      enrollment: resolveCohortState(
        cohort.config,
        new Date(),
        ctx.query.cohortState,
      ),
    },
  }
}
